import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  IconButton,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from "@mui/material";
import { FileText as FileTextIcon, PlusCircle as PlusCircleIcon } from "lucide-react";
import SectionItem from "../components/SectionItem";
import EmptyMessage from "../components/EmptyMessage";
import SectionForm from "../old/SectionForm";
import languageLearningApi from "../../middleware/languageLearningApi";

const headerGradient = "linear-gradient(90deg, #1976d2 0%, #2196f3 100%)";

const SectionsPanel = ({ selectedCourseId, sections, onRefresh, onSelectSection }) => {
  const [showSectionForm, setShowSectionForm] = useState(false);
  const [editSection, setEditSection] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);

  // Compute default order: max order + 1 or 1 if no sections exist.
  const defaultOrder =
    sections && sections.length > 0 ? Math.max(...sections.map(s => s.order)) + 1 : 1;

  const handleAddClick = () => {
    setShowSectionForm(true);
  };

  const handleFormClose = () => {
    setShowSectionForm(false);
    setEditSection(null);
    onRefresh(selectedCourseId);
  };

  const handleEditClick = (section) => {
    setEditSection(section);
    setShowSectionForm(true);
  };

  const handleDeleteClick = (section) => {
    setDeleteCandidate(section);
  };

  const handleDeleteConfirm = async () => {
    try {
      await languageLearningApi.delete(`/sections/${deleteCandidate._id}`);
      setDeleteCandidate(null);
      onRefresh(selectedCourseId);
    } catch (err) {
      console.error("Error deleting section:", err);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteCandidate(null);
  };

  // Cascade update: for an edited section, update all other sections with order ≥ newOrder.
  const handleCascadeUpdate = async (newOrder, editedSectionId) => {
    const updates = sections
      .filter(s => s._id !== editedSectionId && s.order >= newOrder)
      .map(s => {
        const updatedSection = { ...s, order: s.order + 1 };
        return languageLearningApi.put(`/sections/${s._id}`, updatedSection);
      });
    try {
      await Promise.all(updates);
      console.log("Cascade update completed for sections.");
    } catch (err) {
      console.error("Error in cascade update:", err);
    }
  };

  return (
    <>
      <Card 
        elevation={3} 
        sx={{ overflow: 'hidden', height: '100%', opacity: !selectedCourseId ? 0.75 : 1 }}
      >
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FileTextIcon size={20} style={{ marginRight: 8 }} />
              <Typography variant="h6" fontWeight="bold">
                Sections
              </Typography>
            </Box>
          }
          action={
            selectedCourseId && (
              <IconButton color="primary" onClick={handleAddClick}>
                <PlusCircleIcon size={20} />
              </IconButton>
            )
          }
          sx={{ 
            background: headerGradient, 
            color: 'white',
            '& .MuiCardHeader-action': { color: 'white' }
          }}
        />
        <CardContent sx={{ p: 2 }}>
          {!selectedCourseId ? (
            <EmptyMessage
              message="Select a course to view sections"
              showAddButton={false}
            />
          ) : (
            <>
              {showSectionForm && (
                <SectionForm 
                  courseId={selectedCourseId}
                  section={editSection}
                  defaultOrder={defaultOrder}
                  existingSections={sections}
                  onClose={handleFormClose}
                  onCascadeUpdate={handleCascadeUpdate}
                />
              )}
              {sections && sections.length > 0 ? (
                sections.map(section => (
                  <SectionItem 
                    key={section._id} 
                    section={section} 
                    onSelectSection={onSelectSection}
                    onEdit={handleEditClick}
                    onDelete={handleDeleteClick}
                  />
                ))
              ) : (
                <EmptyMessage
                  message="No sections available"
                  showAddButton
                  onAddClick={handleAddClick}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(deleteCandidate)} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete section #{deleteCandidate?.order}: "{deleteCandidate?.title}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">Confirm</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SectionsPanel;
